import { supabase } from "@/integrations/supabase/client";
import type { DisparoMessageType } from "@/lib/disparos-types";

export type DisparoMediaType = Extract<
  DisparoMessageType,
  "image" | "video" | "audio" | "document"
>;

export const DISPARO_MEDIA_ACCEPT: Record<DisparoMediaType, string> = {
  image: "image/jpeg,image/png,image/webp,image/gif",
  video: "video/mp4,video/webm",
  audio: "audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/mp4",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.txt",
};

export const DISPARO_MEDIA_MAX_BYTES = 16 * 1024 * 1024;

/**
 * Links de compartilhamento (Drive/Dropbox) devolvem HTML, não o arquivo:
 * o provedor de WhatsApp não consegue baixar a mídia e o disparo falha.
 */
export function isShareLinkNotDirectFile(url: string): boolean {
  const u = url.trim().toLowerCase();
  if (!u) return false;
  return (
    /drive\.google\.com\/file\//.test(u) ||
    /docs\.google\.com\//.test(u) ||
    /dropbox\.com\/.+\?dl=0/.test(u) ||
    /1drv\.ms|onedrive\.live\.com/.test(u)
  );
}

export async function uploadDisparoMedia(params: {
  workspaceId: string;
  file: File;
  mediaType: DisparoMediaType;
}): Promise<string> {
  const { workspaceId, file, mediaType } = params;

  if (file.size > DISPARO_MEDIA_MAX_BYTES) {
    throw new Error("Arquivo muito grande (máximo 16MB).");
  }

  const { data, error } = await supabase.functions.invoke("presign-disparo-media", {
    body: {
      workspace_id: workspaceId,
      media_type: mediaType,
      mime_type: file.type || "application/octet-stream",
      file_name: file.name,
      size: file.size,
    },
  });

  if (error || !data?.upload_url || !data?.media_url) {
    console.error("presign-disparo-media falhou:", error, data);
    throw new Error("Não foi possível preparar o envio do arquivo. Tente novamente.");
  }

  const put = await fetch(data.upload_url as string, {
    method: "PUT",
    headers: (data.headers as Record<string, string>) ?? { "Content-Type": file.type },
    body: file,
  });

  if (!put.ok) {
    console.error("upload disparo media falhou:", put.status, await put.text().catch(() => ""));
    throw new Error("Falha ao enviar o arquivo. Tente novamente.");
  }

  return data.media_url as string;
}
